import { mapActions } from 'vuex'
import { app } from '@/main'

const RICH_TEXT_EDITOR_TOOLBAR = [
  [
    'bold',
    'italic',
    'underline',
    'strike'
  ],
  [ 'clean' ],
  [ 'link' ]
]

export const isUrlYoutubeOrVimeo = value => {
  // eslint-disable-next-line no-useless-escape
  return !value || /^(http:\/\/|https:\/\/)(vimeo\.com|youtu\.be|www\.youtube\.com)\/([\w\/]+)([\?].*)?$/.test(value)
}

export const isVimeoUrl = value => {
  // eslint-disable-next-line no-useless-escape
  return !value || /^(http|https)?:\/\/(www\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|)(\d+)(?:|\/\?)/.test(value)
}

export const isYoutubeUrl = value => {
  // eslint-disable-next-line no-useless-escape
  return !value || /^(https?\:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/.test(value)
}

export default {
  data () {
    return {
      themeList: [],
      entrepreneurProfileList: [],
      partnersList: [],
      certificateTypeList: [],
      RICH_TEXT_EDITOR_TOOLBAR,

      yesNoOptions: [
        {
          label: app.$t('global:form.tracks.yes'),
          value: true
        },
        {
          label: app.$t('global:form.tracks.no'),
          value: false
        }
      ],

      timeDurationList: [
        {
          text: app.$t('global.time:select.min'),
          value: 'min'
        },
        {
          text: app.$t('global.time:select.h'),
          value: 'h'
        },
        {
          text: app.$t('global.time:select.d'),
          value: 'd'
        },
        {
          text: app.$t('global.time:select.w'),
          value: 'w'
        }
      ],

      certificateOptions: [
        {
          label: app.$t('global:form.tracks.yes'),
          value: true
        },
        {
          label: app.$t('global:form.tracks.no'),
          value: false
        }
      ],

      registrationStatusOptions: [
        {
          text: app.$t('global:form.registration.status.open'),
          value: 'open'
        },
        {
          text: app.$t('global:form.registration.status.closed'),
          value: 'closed'
        }
      ],

      paymentTypeOptions: [
        {
          text: app.$t('global:form.payment.type.free'),
          label: app.$t('global:free'),
          value: 'free'
        },
        {
          text: app.$t('global:form.payment.type.paid'),
          label: app.$t('global:paid'),
          value: 'paid'
        }
      ]

    }
  },

  computed: {
    isEditing () {
      return this.$route.params.id || this.formData.id
    }
  },

  methods: {
    ...mapActions([
      'attemptGetThematicPagesList',
      'attemptGetSolutionsList',
      'attemptGetCertificatesList',
      'attemptGetThemesList',
      'attemptGetEntrepreneurProfilesList',
      'attemptListPartners',
      'attemptGetSurveysList',
      'attemptGetGeneralSettings',
      'attemptGetSasUnityList',
      'attemptGetSasProjectList'
    ]),

    /**
     * Busca todos os dados necessários em paralelo
     * @param {Object} params - Parâmetros customizados para cada busca
     * @param {Array} additionalFetches - Array de promises adicionais (ex: surveys, sas, etc)
     * @returns {Promise}
     */
    async fetchAllData (params = {}, additionalFetches = []) {
      const builded = this.buildFetchParams(params)

      // Buscas principais (comuns aos 3 componentes)
      const mainFetches = [
        this.attemptGetThematicPagesList(builded.thematicPages),
        this.attemptGetCertificatesList(builded.certificates),
        this.attemptGetThemesList(builded.themes),
        this.attemptGetEntrepreneurProfilesList(builded.entrepreneurProfiles),
        this.attemptListPartners(builded.partners)
      ]

      // Buscas opcionais (surveys, sas, general settings)
      const optionalFetches = []

      if (builded.surveys) {
        optionalFetches.push(this.attemptGetSurveysList(builded.surveys))
      }

      if (builded.generalSettings) {
        optionalFetches.push(this.attemptGetGeneralSettings(builded.generalSettings))
      }

      if (builded.sasUnity) {
        optionalFetches.push(this.attemptGetSasUnityList(builded.sasUnity))
      }

      if (builded.sasProject) {
        optionalFetches.push(this.attemptGetSasProjectList(builded.sasProject))
      }

      // Combinar todas as buscas
      const allFetches = [
        ...mainFetches,
        ...optionalFetches,
        ...additionalFetches
      ]

      return Promise.all(allFetches)
        .then((results) => {
          // Separar resultados principais dos opcionais
          const [
            thematicPages,
            certificates,
            themesList,
            entrepreneurList,
            partnersList,
            ...optionalResults
          ] = results

          this.processFetchedData({
            thematicPages,
            certificates,
            themesList,
            entrepreneurList,
            partnersList
          })

          // Processar dados opcionais
          this.processOptionalData(optionalResults, builded)
        })
    },

    /**
     * Constrói os parâmetros para cada busca
     * @param {Object} params - Parâmetros customizados
     * @returns {Object}
     */
    buildFetchParams (params = {}) {
      const builded = {
        thematicPages: this.thematicPagesPagination || {
          query: {
            name: null,
            limit: 300
          },
          filter: { active: 1 },
          order: { name: 'ASC' },
          lastPage: null,
          limit: 6,
          page: 1
        },
        certificates: { filter: { active: 1 } },
        themes: { filter: { active: 1 } },
        entrepreneurProfiles: { filter: { active: 1 } },
        partners: { filter: { active: 1 } }
      }

      // Parâmetros opcionais (para Solutions e Programs)
      if (params.surveys !== undefined || params.includeSurveys) {
        builded.surveys = { filter: { active: 1 } }
      }

      if (params.generalSettings !== undefined || params.includeGeneralSettings) {
        builded.generalSettings = {}
      }

      if (params.sasUnity !== undefined || params.includeSasUnity) {
        builded.sasUnity = { filter: { active: 1 } }
      }

      if (params.sasProject !== undefined || params.includeSasProject) {
        builded.sasProject = { filter: { active: 1 } }
      }

      // Merge com parâmetros customizados
      Object.keys(builded).map(key => {
        if (params && params[key]) {
          builded[key] = {
            ...builded[key],
            ...params[key]
          }
        }

        return key
      })

      return builded
    },

    /**
     * Processa os dados retornados das buscas
     * @param {Object} data - Dados retornados
     */
    processFetchedData ({
      thematicPages,
      certificates,
      themesList,
      entrepreneurList,
      partnersList
    }) {
      // Processar Thematic Pages
      if (thematicPages.data) {
        this.thematicPages = []

        if (this.thematicPagesPagination) {
          this.thematicPagesPagination.lastPage = thematicPages.lastPage
          this.thematicPagesPagination.page = thematicPages.actualPage
        }

        thematicPages.data.forEach(element => {
          this.thematicPages.push({
            name: element.name,
            id: element.id,
            active: element.active,
            thematicPagesProgram: element.thematicPagesProgram,
            thematicPageLearningPaths: element.thematicPageLearningPaths,
            thematicPageSolutions: element.thematicPageSolutions
          })
        })
      }

      // Processar Certificates
      if (certificates.data) {
        this.certificateTypeList = []

        certificates.data.forEach(element => {
          this.certificateTypeList.push({
            text: element.name,
            value: element.id
          })
        })
      }

      // Processar Themes
      if (themesList.data) {
        this.themeList = []

        themesList.data.forEach(element => {
          this.themeList.push({
            text: element.name,
            value: element.alias
          })
        })
      }

      // Processar Entrepreneur Profiles
      if (entrepreneurList.data) {
        this.entrepreneurProfileList = []

        entrepreneurList.data.forEach(element => {
          this.entrepreneurProfileList.push({
            text: element.name,
            value: element.id
          })
        })
      }

      // Processar Partners
      if (partnersList.data) {
        this.partnersList = []

        partnersList.data.data.forEach(element => {
          this.partnersList.push({
            text: element.name,
            value: element.id
          })
        })
      }
    },

    /**
     * Processa dados opcionais (surveys, SAS, general settings)
     * @param {Array} optionalResults - Array com resultados das buscas opcionais
     * @param {Object} builded - Objeto com parâmetros das buscas realizadas
     */
    processOptionalData (optionalResults, builded) {
      let resultIndex = 0

      // Processar Surveys (se solicitado)
      if (builded.surveys && optionalResults[resultIndex]) {
        const surveys = optionalResults[resultIndex]

        if (this.satisfactionSurveyList !== undefined) {
          this.satisfactionSurveyList = []
        }

        if (this.subscriptionSurveyList !== undefined) {
          this.subscriptionSurveyList = []
        }

        if (surveys.data && surveys.data.length) {
          surveys.data.forEach(element => {
            const surveyItem = {
              text: element.name,
              value: element.id
            }

            // Para Programs e Solutions
            const hasType = typeof element.type === 'string' && element.type.length > 0

            if (!hasType) {
              // Comportamento anterior: quando não há tipo, adicionar em ambas as listas existentes
              if (this.satisfactionSurveyList !== undefined) {
                this.satisfactionSurveyList.push({ ...surveyItem })
              }

              if (this.subscriptionSurveyList !== undefined) {
                this.subscriptionSurveyList.push({ ...surveyItem })
              }
            } else {
              const type = element.type

              if (this.satisfactionSurveyList !== undefined &&
                (type === 'satisfaction' || type === 'SATISFACTION')) {
                this.satisfactionSurveyList.push({ ...surveyItem })
              }

              if (this.subscriptionSurveyList !== undefined &&
                (type === 'subscription' || type === 'SUBSCRIPTION')) {
                this.subscriptionSurveyList.push({ ...surveyItem })
              }
            }
          })
        }

        resultIndex++
      }

      // Processar General Settings (se solicitado)
      if (builded.generalSettings && optionalResults[resultIndex]) {
        const settings = optionalResults[resultIndex]

        if (settings.data && settings.data.length && this.configurationList !== undefined) {
          this.configurationList = settings.data.map(item => ({
            text: item.key,
            value: item.value
          }))

          this.setMinimumSolutionPrice()
        }

        resultIndex++
      }

      // Processar SAS Unity (se solicitado - apenas Solutions)
      if (builded.sasUnity && optionalResults[resultIndex]) {
        const sasUnity = optionalResults[resultIndex]

        if (this.sasUnityList !== undefined) {
          this.sasUnityList = []

          if (sasUnity.data && sasUnity.data.length) {
            sasUnity.data.forEach(element => {
              this.sasUnityList.push({
                text: element.name,
                value: element.id
              })
            })
          }
        }

        resultIndex++
      }

      // Processar SAS Project (se solicitado - apenas Solutions)
      if (builded.sasProject && optionalResults[resultIndex]) {
        const sasProject = optionalResults[resultIndex]

        if (this.sasProjectList !== undefined) {
          this.sasProjectList = []

          if (sasProject.data && sasProject.data.length) {
            sasProject.data.forEach(element => {
              this.sasProjectList.push({
                text: element.name,
                value: element.id
              })
            })
          }
        }

        resultIndex++
      }
    },
    setMinimumSolutionPrice () {
      const filter = this.configurationList.filter(item => item.text === 'financial_credit_card_minimum_installment_value')

      this.minimumSolutionPrice = filter.length ? parseFloat(filter[0].value) : 0.0
    },
    /**
     * Monta objeto de workload a partir de string
     * @param {string} workload - String de workload (ex: "30min", "2h", "5d")
     * @returns {Object} - Objeto com type e number
     */
    mountWorkloadObject (workload) {
      return {
        type: workload ? workload.match(/[hdw|min]+/gi) && workload.match(/[hdw|min]+/gi)[0] : 'h',
        number: workload ? workload.match(/[0-9]+/gi) && workload.match(/[0-9]+/gi)[0] : 0
      }
    },

    isUrlYoutubeOrVimeo,
    isVimeoUrl,
    isYoutubeUrl
  }
}
